import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import logo from "@/assets/bubble-radar-logo.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = logo;

export default function SignupPage() {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resent, setResent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!email.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!password || password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      const resp = await axios.post(
        `${API}/auth/register`,
        { name: name.trim(), company: company.trim() || null, email, password },
      );
      setResent(!!resp.data?.resent);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to create account");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md -mt-6">
        <div className="flex justify-center mb-10" data-testid="signup-logo-wrapper">
          <img
            src={LOGO_URL}
            alt="Bubble Radar logo"
            data-testid="signup-logo"
            className="h-40 w-auto"
          />
        </div>
        <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-lg" data-testid="signup-title">Create account</CardTitle>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-4" data-testid="signup-success-state">
              <p className="text-sm text-slate-600" data-testid="signup-success-message">
                {resent
                  ? "Verification link resent. Check your email to activate your account."
                  : "We sent a verification link to your email. Please verify to activate your account."}
              </p>
              <Button asChild className="w-full bg-slate-900 hover:bg-slate-800" data-testid="signup-go-to-login">
                <Link to="/login">Go to sign in</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="signup-name" className="text-sm text-slate-600">Name</label>
                <Input
                  id="signup-name"
                  data-testid="signup-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="signup-company" className="text-sm text-slate-600">Company (optional)</label>
                <Input
                  id="signup-company"
                  data-testid="signup-company-input"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="signup-email" className="text-sm text-slate-600">Email</label>
                <Input
                  id="signup-email"
                  data-testid="signup-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="signup-password" className="text-sm text-slate-600">Password</label>
                <Input
                  id="signup-password"
                  data-testid="signup-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="signup-submit-button"
                className="w-full bg-slate-900 hover:bg-slate-800"
                disabled={loading}
              >
                {loading ? "Creating..." : "Create account"}
              </Button>
              <div className="text-sm text-slate-600 text-center">
                <Link to="/login" className="hover:text-slate-900" data-testid="signup-login-link">
                  Already have an account? Sign in
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  </div>
  );
}
